/**
 * Enhanced Single DateTime Component - Balanced Modern Design
 * A modern date-time picker with hours/minutes support.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDateRange.css';
import helpers from '../assets/js/helper';

// Reuse the same styled components for design consistency
const GlassContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexGrow: 1,
    position: 'relative',
    left: '4px',
    gap: 10,
    padding: '12px 14px',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: 12,
    border: '1px solid rgba(99, 102, 241, 0.1)',
    boxShadow: '0 2px 12px rgba(99, 102, 241, 0.06)',
    transition: 'all 0.2s ease',
}));

const DateTimeField = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
}));

const FieldLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    color: '#fff',
    fontFamily: 'Cairo, sans-serif',
    minWidth: 34,
    width: 'fit-content',
    px: 1.5,
    height: 34,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.25)',
    whiteSpace: 'nowrap',
}));

const StyledInputWrapper = styled(Box)(({ focused }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 10,
    background: focused ? 'rgba(99, 102, 241, 0.06)' : '#f1f5f9',
    border: `1.5px solid ${focused ? '#6366f1' : 'transparent'}`,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    flex: 1,
    minHeight: 40,
    '&:hover': {
        background: 'rgba(99, 102, 241, 0.05)',
        borderColor: 'rgba(99, 102, 241, 0.25)',
    },
}));

const TimeSelect = styled('select')(({ theme }) => ({
    appearance: 'none',
    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
    border: '1.5px solid #a5b4fc',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#4f46e5',
    cursor: 'pointer',
    outline: 'none',
    textAlign: 'center',
    minWidth: 48,
    transition: 'all 0.15s ease',
    '&:focus': {
        borderColor: '#6366f1',
        boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.15)',
    },
}));

const CustomTimeInput = ({ value, onChange }) => {
    const hours = value?.split(':')?.[0] || '00';
    const minutes = value?.split(':')?.[1] || '00';

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 1.25,
            borderTop: '1px solid #e2e8f0',
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        }}>
            <AccessTimeIcon sx={{ color: '#6366f1', fontSize: 18 }} />
            <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 600, fontFamily: 'Cairo' }}>الوقت:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeSelect
                    value={minutes}
                    onChange={(e) => onChange(`${hours}:${e.target.value.padStart(2, '0')}`)}
                >
                    {[...Array(60).keys()].map((minute) => (
                        <option key={minute} value={minute.toString().padStart(2, '0')}>
                            {minute.toString().padStart(2, '0')}
                        </option>
                    ))}
                </TimeSelect>
                <Typography sx={{ fontWeight: 800, color: '#6366f1', fontSize: '14px' }}>:</Typography>
                <TimeSelect
                    value={hours}
                    onChange={(e) => onChange(`${e.target.value.padStart(2, '0')}:${minutes}`)}
                >
                    {[...Array(24).keys()].map((hour) => (
                        <option key={hour} value={hour.toString().padStart(2, '0')}>
                            {hour.toString().padStart(2, '0')}
                        </option>
                    ))}
                </TimeSelect>
            </Box>
        </Box>
    );
};

const CustomDateTimePicker = ({
    value,
    onChange,
    label,
    placeholder,
    onClearClick,
}) => {
    const [t] = useTranslation("common");
    const displayLabel = label || t('GLOBAL.choose_date') || 'اختر التاريخ';
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!document.getElementById('datepicker-portal')) {
            const portalDiv = document.createElement('div');
            portalDiv.id = 'datepicker-portal';
            document.body.appendChild(portalDiv);
        }
    }, []);

    const formatDisplayDate = (date) => {
        if (!date) return null;
        try {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            const timeStr = `${hours}:${minutes}`;
            return { dateStr, timeStr };
        } catch {
            return null;
        }
    };

    const DateInputButton = React.forwardRef(({ value: val, onClick }, ref) => {
        const displayData = formatDisplayDate(value);
        return (
            <StyledInputWrapper
                ref={ref}
                onClick={onClick}
                focused={focused ? 1 : 0}
            >
                <CalendarMonthIcon sx={{ color: focused ? '#6366f1' : '#94a3b8', fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    {displayData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                            <Typography sx={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: '#1e293b',
                                fontFamily: 'Cairo, monospace',
                            }}>
                                {displayData.dateStr}
                            </Typography>
                            <Typography sx={{
                                fontSize: '11px',
                                color: '#6366f1',
                                fontWeight: 600,
                                fontFamily: 'Cairo, monospace',
                            }}>
                                {displayData.timeStr}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            fontFamily: 'Cairo',
                            fontWeight: 500
                        }}>
                            {placeholder || t('GLOBAL.choose_date') || 'اختر التاريخ'}
                        </Typography>
                    )}
                </Box>
                {displayData && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClearClick && onClearClick();
                        }}
                        sx={{
                            p: 0.5,
                            color: '#94a3b8',
                            '&:hover': {
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                            }
                        }}
                    >
                        <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </StyledInputWrapper>
        );
    });

    return (
        <GlassContainer elevation={0}>
            <Box sx={{ pt: 0 }}>
                <DateTimeField sx={{ width: '100%' }}>
                    <FieldLabel>{displayLabel}</FieldLabel>
                    <Box sx={{ flex: 1, width: '100%' }}>
                        <DatePicker
                            selected={value ? new Date(value) : null}
                            onChange={(date) => onChange && onChange(date)}
                            showTimeInput
                            dateFormat="yyyy-MM-dd HH:mm"
                            customInput={<DateInputButton />}
                            customTimeInput={<CustomTimeInput />}
                            calendarClassName="datetime-pro-calendar"
                            popperProps={{ strategy: 'fixed' }}
                            popperPlacement="bottom-start"
                            portalId="datepicker-portal"
                            onCalendarOpen={() => setFocused(true)}
                            onCalendarClose={() => setFocused(false)}
                        />
                    </Box>
                </DateTimeField>
            </Box>
        </GlassContainer>
    );
};

export default CustomDateTimePicker;
